import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  alpha,
  useTheme,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import {
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  Warning as WarningIcon,
  AccessTime as DurationIcon,
  Healing as RecoveryIcon,
  LocalHospital as InterventionIcon,
  Notes as NotesIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { QK } from "../../../../../../base/qk";
import {
  getSeizureById,
  updateSeizure,
} from "../../../../../../api/services/observations";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import CustomDateField from "../../../../../FormFields/CustomDateField";
import CustomTimePicker from "../../../../../FormFields/TiimePicker/TimePicker";

const splitDateTime = (dateTime) => {
  if (!dateTime) return { date: "", time: "" };
  try {
    const d = new Date(dateTime);
    if (isNaN(d.getTime())) return { date: "", time: "" };
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${min}` };
  } catch {
    return { date: "", time: "" };
  }
};

const EditSeizureModal = ({ show, handleClose, selectedSeizure, refetch }) => {
  const theme = useTheme();
  const { id: clientId } = useParams();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    date: "",
    time: "",
    location: "",
    description: "",
    possible_triggers: "",
    warning_aura: "",
    user_feeling: "",
    during_seizure: "",
    seizure_length: "",
    after_seizure: "",
    recovery_period: "",
    staff_intervention: "",
    action_taken: "",
    founder: "",
    handover: false,
    manager: false,
  });

  const triggerOptions = [
    { value: "Unknown", label: "Unknown" },
    { value: "illness", label: "Illness" },
    { value: "Constipation", label: "Constipation" },
    { value: "Sleep (pattern)", label: "Sleep (pattern)" },
    { value: "Changes in medication", label: "Changes in medication" },
  ];

  const warningOptions = [
    { value: "none", label: "None" },
    { value: "Behaviour Changes", label: "Behaviour Changes" },
    { value: "Lethargy", label: "Lethargy" },
    { value: "Mood Change", label: "Mood Change" },
  ];

  const duringOptions = [
    { value: "Mumbling (Automatism)", label: "Mumbling (Automatism)" },
    { value: "Confusion (Automatism)", label: "Confusion (Automatism)" },
    { value: "Wandering around", label: "Wandering around" },
    { value: "Laughing (Automatism)", label: "Laughing (Automatism)" },
  ];

  const afterOptions = [
    { value: "Immediate recovery", label: "Immediate recovery" },
    { value: "Confusion", label: "Confusion" },
    { value: "Slept", label: "Slept" },
    { value: "Aggressive", label: "Aggressive" },
    {
      value: "Automatisms (record which)",
      label: "Automatisms (record which)",
    },
  ];

  const interventionOptions = [
    { value: "PRN given", label: "PRN given" },
    { value: "Gp visit", label: "Gp visit" },
    { value: "Ambulance called", label: "Ambulance called" },
    { value: "Hospital admission", label: "Hospital admission" },
    { value: "Other (record below)", label: "Other (record below)" },
  ];

  const {
    data: seizure,
    isLoading,
    refetch: refetchSeizure,
  } = useQuery(
    [QK.seizure, selectedSeizure],
    () => getSeizureById(selectedSeizure),
    {
      enabled: !!selectedSeizure && show,
      onSuccess: (data) => {
        if (data) {
          const { date, time } = splitDateTime(data.date_time);
          setFormData({
            date,
            time,
            location: data.location || "",
            description: data.description || "",
            possible_triggers: data.possible_triggers || "",
            warning_aura: data.warning_aura || "",
            user_feeling: data.user_feeling || "",
            during_seizure: data.during_seizure || "",
            seizure_length: data.seizure_length || "",
            after_seizure: data.after_seizure || "",
            recovery_period: data.recovery_period || "",
            staff_intervention: data.staff_intervention || "",
            action_taken: data.action_taken || "",
            founder: data.founder || "",
            handover: data.handover || false,
            manager: data.manager || false,
          });
        }
      },
    }
  );

  useEffect(() => {
    if (show && selectedSeizure) {
      refetchSeizure();
    }
  }, [show, selectedSeizure, refetchSeizure]);

  const editSeizureMutation = useMutation(
    (newData) => updateSeizure(selectedSeizure, newData),
    {
      onSuccess: () => {
        enqueueSnackbar("Seizure observation updated successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries([QK.seizure]);
        queryClient.invalidateQueries([QK.seizures, clientId]);
        handleClose();
        refetch();
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message ||
            "Failed to update seizure observation",
          { variant: "error" }
        );
      },
    }
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const combinedDateTime = formData.date && formData.time
      ? `${formData.date}T${formData.time}:00`
      : "";

    const submitData = {
      date_time: combinedDateTime,
      location: formData.location,
      description: formData.description,
      possible_triggers: formData.possible_triggers,
      warning_aura: formData.warning_aura,
      user_feeling: formData.user_feeling,
      during_seizure: formData.during_seizure,
      seizure_length: formData.seizure_length,
      after_seizure: formData.after_seizure,
      recovery_period: formData.recovery_period,
      staff_intervention: formData.staff_intervention,
      action_taken: formData.action_taken,
      founder: formData.founder,
      handover: formData.handover,
      manager: formData.manager,
      client: clientId,
    };
    editSeizureMutation.mutate(submitData);
  };

  const handleDialogClose = () => {
    setFormData({
      date: "",
      time: "",
      location: "",
      description: "",
      possible_triggers: "",
      warning_aura: "",
      user_feeling: "",
      during_seizure: "",
      seizure_length: "",
      after_seizure: "",
      recovery_period: "",
      staff_intervention: "",
      action_taken: "",
      founder: "",
      handover: false,
      manager: false,
    });
    handleClose();
  };

  if (isLoading) {
    return (
      <Dialog open={show} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogContent
          sx={{ display: "flex", justifyContent: "center", py: 8 }}
        >
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  const cardSx = {
    p: 1.75,
    borderRadius: "10px",
    border: "1px solid #EAECF0",
    bgcolor: "#FFFFFF",
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column",
    flex: "1 1 220px",
    minWidth: 0,
  };

  const fieldHeaderSx = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 1.25,
  };

  const iconBoxSx = (color) => ({
    width: 26,
    height: 26,
    borderRadius: "7px",
    bgcolor: alpha(color, 0.1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  });

  const labelSx = {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#344054",
  };

  const requiredBadgeSx = {
    fontSize: "0.65rem",
    fontWeight: 600,
    color: "#F04438",
    ml: "auto",
  };

  return (
    <Dialog
      open={show}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          maxHeight: "92vh",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon
                sx={{ color: theme.palette.primary.main, fontSize: 20 }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  color: "#101828",
                  letterSpacing: "-0.01em",
                }}
              >
                Edit Seizure Observation
              </Typography>
              <Typography
                sx={{ fontSize: "0.7rem", color: "#667085", mt: 0.2 }}
              >
                Update seizure chart details
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleDialogClose}
            size="small"
            sx={{
              color: "#98A2B3",
              "&:hover": { bgcolor: "#F2F4F7", color: "#475467" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: "#EAECF0" }} />
      </DialogTitle>

      <form onSubmit={handleSubmit} style={{ width: "100%" }}>
        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              width: "100%",
            }}
          >
            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#2469F0", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#2469F0", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#2469F0")}>
                  <CalendarIcon sx={{ color: "#2469F0", fontSize: 16 }} />
                </Box>
                <Typography sx={labelSx}>Date</Typography>
                <Typography sx={requiredBadgeSx}>Required</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomDateField
                  name="date"
                  label=""
                  value={formData.date}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </Box>
            </Box>

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#8B5CF6", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#8B5CF6", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#8B5CF6")}>
                  <TimeIcon sx={{ color: "#8B5CF6", fontSize: 16 }} />
                </Box>
                <Typography sx={labelSx}>Time</Typography>
                <Typography sx={requiredBadgeSx}>Required</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomTimePicker
                  name="time"
                  label=""
                  value={formData.time}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </Box>
            </Box>

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#8B5CF6", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#8B5CF6", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#8B5CF6")}>
                  <LocationIcon sx={{ color: "#8B5CF6", fontSize: 16 }} />
                </Box>
                <Typography sx={labelSx}>Location</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomInput
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g. Living Room"
                  fullWidth
                />
              </Box>
            </Box>

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#F59E0B", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#F59E0B", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#F59E0B")}>
                  <DescriptionIcon
                    sx={{ color: "#F59E0B", fontSize: 16 }}
                  />
                </Box>
                <Typography sx={labelSx}>Description of Seizure</Typography>
                <Typography sx={requiredBadgeSx}>Required</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomInput
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </Box>
            </Box>

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#2469F0", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#2469F0", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#2469F0")}>
                  <WarningIcon sx={{ color: "#2469F0", fontSize: 16 }} />
                </Box>
                <Typography sx={labelSx}>Possible Triggers</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomSelect
                  name="possible_triggers"
                  options={triggerOptions}
                  value={formData.possible_triggers}
                  onChange={handleChange}
                  placeholder="Select"
                  fullWidth
                  searchable
                  searchPlaceholder="Search trigger..."
                />
              </Box>
            </Box>

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#F59E0B", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#F59E0B", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#F59E0B")}>
                  <WarningIcon sx={{ color: "#F59E0B", fontSize: 16 }} />
                </Box>
                <Typography sx={labelSx}>Warning / Aura</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomSelect
                  name="warning_aura"
                  options={warningOptions}
                  value={formData.warning_aura}
                  onChange={handleChange}
                  placeholder="Select"
                  fullWidth
                  searchable
                  searchPlaceholder="Search warning..."
                />
              </Box>
            </Box>

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#8B5CF6", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#8B5CF6", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#8B5CF6")}>
                  <PersonIcon sx={{ color: "#8B5CF6", fontSize: 16 }} />
                </Box>
                <Typography sx={labelSx}>How User Felt</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomInput
                  name="user_feeling"
                  value={formData.user_feeling}
                  onChange={handleChange}
                  fullWidth
                />
              </Box>
            </Box>

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#2469F0", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#2469F0", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#2469F0")}>
                  <DurationIcon sx={{ color: "#2469F0", fontSize: 16 }} />
                </Box>
                <Typography sx={labelSx}>During Seizure</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomSelect
                  name="during_seizure"
                  options={duringOptions}
                  value={formData.during_seizure}
                  onChange={handleChange}
                  placeholder="Select"
                  fullWidth
                  searchable
                  searchPlaceholder="Search during..."
                />
              </Box>
            </Box>

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#10B981", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#10B981", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#10B981")}>
                  <DurationIcon sx={{ color: "#10B981", fontSize: 16 }} />
                </Box>
                <Typography sx={labelSx}>Length (Minutes)</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomInput
                  name="seizure_length"
                  type="number"
                  value={formData.seizure_length}
                  onChange={handleChange}
                  fullWidth
                />
              </Box>
            </Box>

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#10B981", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#10B981", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#10B981")}>
                  <RecoveryIcon sx={{ color: "#10B981", fontSize: 16 }} />
                </Box>
                <Typography sx={labelSx}>After Seizure</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomSelect
                  name="after_seizure"
                  options={afterOptions}
                  value={formData.after_seizure}
                  onChange={handleChange}
                  placeholder="Select"
                  fullWidth
                  searchable
                  searchPlaceholder="Search after..."
                />
              </Box>
            </Box>

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#10B981", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#10B981", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#10B981")}>
                  <RecoveryIcon sx={{ color: "#10B981", fontSize: 16 }} />
                </Box>
                <Typography sx={labelSx}>Recovery Period</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomInput
                  name="recovery_period"
                  value={formData.recovery_period}
                  onChange={handleChange}
                  fullWidth
                />
              </Box>
            </Box>

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#8B5CF6", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#8B5CF6", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#8B5CF6")}>
                  <InterventionIcon
                    sx={{ color: "#8B5CF6", fontSize: 16 }}
                  />
                </Box>
                <Typography sx={labelSx}>Staff Interventions</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomSelect
                  name="staff_intervention"
                  options={interventionOptions}
                  value={formData.staff_intervention}
                  onChange={handleChange}
                  placeholder="Select"
                  fullWidth
                  searchable
                  searchPlaceholder="Search intervention..."
                />
              </Box>
            </Box>

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#8B5CF6", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#8B5CF6", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#8B5CF6")}>
                  <InterventionIcon
                    sx={{ color: "#8B5CF6", fontSize: 16 }}
                  />
                </Box>
                <Typography sx={labelSx}>Founder / Other</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomInput
                  name="founder"
                  value={formData.founder}
                  onChange={handleChange}
                  placeholder="Additional details"
                  fullWidth
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              ...cardSx,
              flex: "1 1 100%",
              mt: 2,
              "&:hover": {
                borderColor: alpha("#F59E0B", 0.4),
                boxShadow: `0px 2px 8px ${alpha("#F59E0B", 0.08)}`,
              },
            }}
          >
            <Box sx={fieldHeaderSx}>
              <Box sx={iconBoxSx("#F59E0B")}>
                <NotesIcon sx={{ color: "#F59E0B", fontSize: 16 }} />
              </Box>
              <Typography sx={labelSx}>Action Taken & Comments</Typography>
            </Box>
            <CustomTextArea
              name="action_taken"
              value={formData.action_taken}
              onChange={handleChange}
              minRows={2}
              maxRows={4}
              fullWidth
              placeholder="Add any notes..."
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexWrap: "wrap",
              p: 1.75,
              borderRadius: "10px",
              border: "1px solid #EAECF0",
              bgcolor: "#FFFFFF",
              mt: 2,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  name="manager"
                  checked={formData.manager}
                  onChange={handleChange}
                  size="small"
                  sx={{
                    color: "#D0D5DD",
                    "&.Mui-checked": { color: "#2469F0" },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "#344054",
                    fontWeight: 500,
                  }}
                >
                  Share with Manager
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="handover"
                  checked={formData.handover}
                  onChange={handleChange}
                  size="small"
                  sx={{
                    color: "#D0D5DD",
                    "&.Mui-checked": { color: "#2469F0" },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "#344054",
                    fontWeight: 500,
                  }}
                >
                  Handover
                </Typography>
              }
            />
          </Box>
        </DialogContent>

        <Divider sx={{ borderColor: "#EAECF0" }} />

        <DialogActions sx={{ p: 2.5, gap: 1.5 }}>
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            disabled={editSeizureMutation.isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              borderRadius: "8px",
              borderColor: "#D0D5DD",
              color: "#344054",
              px: 2.5,
              height: 38,
              "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={editSeizureMutation.isLoading}
            startIcon={
              editSeizureMutation.isLoading ? (
                <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
              ) : (
                <SaveIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              borderRadius: "8px",
              bgcolor: "#2469F0",
              height: 38,
              px: 3,
              boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 2px 6px rgba(36, 105, 240, 0.25)",
              },
            }}
          >
            {editSeizureMutation.isLoading ? "Saving..." : "Update Seizure"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditSeizureModal;